const fetchS3Data = (
  contentType: string,
  identifier: string,
  locale: string,
  version: number | string,
  s3Bucket: string
) => {
  const path = `${s3Bucket}/${contentType}/${stringToLowerSnakeCase(
    identifier
  )}/${locale}/data_${version}.json`;
  return new Promise((resolve, reject) => {
    const cachedEntry = readFromCache(path);
    if (cachedEntry) {
      resolve(cachedEntry);
    }

    fetch(path)
      .then((result) => result.json())
      .then(
        (result) => {
          writeToCache(path, result, 86400000); // ttl in ms, 24h
          return resolve(result);
        },
        (err) => {
          console.error(`Failed S3 content fetching: ${JSON.stringify(err)}`);
          console.warn(
            'Error fetching S3 entries, please check your configuration and the spelling of the identifiers/locale you provided!'
          );
          return reject();
        }
      );
  });
};

export const fetchS3Entries = async (
  entries: {
    contentType: string;
    identifier: string;
    version: number | string;
  }[],
  options?: {
    s3Bucket?: string;
    locale?: string;
    component?: {
      name: string;
      version?: number;
    };
  }
) => {
  const config = {
    s3Bucket: 'https://contextualnavigation.api.community.sap.com',
    locale: 'en-us',
    component: {
      name: 'header',
      version: 0
    },
    ...options
  };

  switch (config.component.name) {
    case 'header':
      entries = entries.filter(function (obj) {
        return obj.contentType !== 'navigation_explore_sap';
      });

      entries.push({
        contentType: 'navigation_explore_sap',
        identifier: 'explore_sap',
        version: 'latest'
      });
      entries.push({
        contentType: 'translations',
        identifier: 'navigation_labels',
        version: 'latest'
      });
      break;
  }

  const promises = entries.map((entry) => {
    const identifier = stringToLowerSnakeCase(entry.identifier);

    return fetchS3Data(
      entry.contentType,
      identifier,
      config.locale,
      entry.version,
      config.s3Bucket
    );
  });

  return Promise.all(promises)
    .then((s3Data) => Object.assign({}, ...s3Data))
    .catch((error) => {
      console.error(error);
      console.warn(
        'Error fetching S3 entries, please check your configuration and the spelling of the identifiers you provided!'
      );
      console.warn('entries:', entries);
      console.warn('options:', options);
    });
};

export const fetchHeaderS3Data = (config: {
  s3Bucket: string;
  landingZoneNavVersion: string;
  touchpointConfigVersion: string;
  landingZoneIdentifier: string;
  touchpointIdentifier: string;
  locale: string;
}) => {
  const entries = [
    {
      contentType: 'navigation_landing_zone',
      identifier: config.landingZoneIdentifier,
      version: config.landingZoneNavVersion
    },
    {
      contentType: 'navigation_touchpoint_configuration',
      identifier: config.touchpointIdentifier,
      version: config.touchpointConfigVersion
    },
    {
      contentType: 'navigation_explore_sap',
      identifier: 'explore_sap',
      version: 'latest'
    },
    {
      contentType: 'translations',
      identifier: 'navigation_labels',
      version: 'latest'
    }
  ];
  const promises = entries.map(({ contentType, identifier, version }) =>
    fetchS3Data(contentType, identifier, config.locale, version, config.s3Bucket)
  );

  return Promise.allSettled(promises)
    .then((s3Data) => {
      const data = s3Data
        .filter((data) => data.status === 'fulfilled')
        .map((data: PromiseFulfilledResult<Object>) => data.value);

      return Object.assign({}, ...data);
    })
    .catch((err) => console.error(err));
};

const writeToCache = (url, data, ttl) => {
  const now = new Date();
  const item = {
    data,
    expiry: now.getTime() + ttl
  };

  localStorage?.setItem(url, JSON.stringify(item));
};

const readFromCache = (url) => {
  const storedItem = localStorage?.getItem(url);

  if (!storedItem) {
    return null;
  }

  const item = JSON.parse(storedItem);
  const now = new Date();

  if (now.getTime() > item.expiry) {
    localStorage.removeItem(url);
    return null;
  }
  return item.data;
};

const stringToLowerSnakeCase = (text: string) => {
  return text.replace(/\s+/g, '_').toLowerCase();
};
